/**
 * Single source of truth for every closed vocabulary in the system.
 * The DB package builds pg enums from these arrays; the API and web
 * validate against the same values, so drift is a compile error.
 */

export const COUNTRIES = ["MA", "MR"] as const;
export type Country = (typeof COUNTRIES)[number];

export const LOCALES = ["fr", "ar", "en"] as const;
export type Locale = (typeof LOCALES)[number];

/** Platform-level role. Hosting is a capability (host_profiles), not a role. */
export const PLATFORM_ROLES = ["user", "staff", "root"] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/**
 * Who decides on a listing's stays.
 *  platform  Sakani's own property: any staff member confirms its bookings (via the house account).
 *  host      A third party's property: ONLY that host confirms. Staff never touch its stays.
 */
export const OWNER_TYPES = ["platform", "host"] as const;
export type OwnerType = (typeof OWNER_TYPES)[number];

/** Fixed id of the house account that owns all platform listings. */
export const HOUSE_HOST_ID = "house";

export const HOST_STATUSES =["pending", "verified", "suspended"] as const;
export type HostStatus = (typeof HOST_STATUSES)[number];

export const VERIFICATION_STATUSES = ["pending", "approved", "rejected"] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

export const ID_DOC_TYPES = ["cin", "passport", "residence_card"] as const;
export type IdDocType = (typeof ID_DOC_TYPES)[number];

export const LISTING_STATUSES = ["draft", "pending_review", "published", "unlisted", "suspended"] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];

export const PROPERTY_TYPES = [
  "apartment",
  "house",
  "villa",
  "riad",
  "guesthouse",
  "hotel_room",
  "desert_camp",
  "kasbah",
  "other",
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const ROOM_TYPES = ["entire_place", "private_room", "shared_room"] as const;
export type RoomType = (typeof ROOM_TYPES)[number];

export const AMENITY_CATEGORIES = ["essentials", "kitchen", "comfort", "outdoor", "safety", "accessibility"] as const;
export type AmenityCategory = (typeof AMENITY_CATEGORIES)[number];

export const CANCELLATION_POLICIES = ["flexible", "moderate", "strict", "non_refundable"] as const;
export type CancellationPolicy = (typeof CANCELLATION_POLICIES)[number];

/**
 * Booking lifecycle.
 *  requested        guest asked; host must respond; dates held
 *  awaiting_payment availability confirmed; guest must pay; dates held
 *  confirmed        deposit verified; dates held
 *  in_stay          guest checked in (keys handed over)
 *  completed        stay finished
 *  declined | expired | cancelled_by_guest | cancelled_by_host   terminal, dates released
 */
export const BOOKING_STATUSES = [
  "requested",
  "awaiting_payment",
  "confirmed",
  "in_stay",
  "completed",
  "declined",
  "expired",
  "cancelled_by_guest",
  "cancelled_by_host",
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/** Statuses during which a booking occupies its dates. */
export const HOLDING_BOOKING_STATUSES = ["requested", "awaiting_payment", "confirmed", "in_stay"] as const satisfies readonly BookingStatus[];

export const HOLD_KINDS = ["booking", "host_block", "external", "maintenance"] as const;
export type HoldKind = (typeof HOLD_KINDS)[number];

export const PAYMENT_KINDS = ["deposit", "balance", "refund"] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];

export const PAYMENT_METHODS = ["bankily", "sadad", "bank_transfer", "cash", "card"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_STATUSES = ["due", "submitted", "verified", "rejected", "void"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/**
 * How much of the stay price is collected online, by Sakani.
 *  platform_fee  online = Sakani's revenue only; the rest is paid to the host in cash. Sakani never holds host funds.
 *  percent       online = a percentage of the total; Sakani owes the host the remainder after its fee (payout).
 *  full          online = everything; Sakani pays the host out after check-in.
 */
export const COLLECTION_MODES = ["platform_fee", "percent", "full"] as const;
export type CollectionMode = (typeof COLLECTION_MODES)[number];

export const PAYOUT_STATUSES = ["pending", "paid", "failed"] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];

export const REVIEW_DIRECTIONS = ["guest_to_host", "host_to_guest"] as const;
export type ReviewDirection = (typeof REVIEW_DIRECTIONS)[number];

export const NOTIFICATION_CHANNELS = ["whatsapp", "email", "push"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const OUTBOX_STATUSES = ["pending", "sent", "failed", "manual"] as const;
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number];

